/**
 * Tiny in-memory TTL cache for SSR loader data.
 *
 * Route loaders run on every server render, and repeated database / edge
 * function round-trips are the main cause of slow TTFB. Caching the results
 * for a short window inside the server isolate keeps HTML generation fast
 * without serving stale content for long.
 */
type Entry<T> = { value: T; expires: number };

const store = new Map<string, Entry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();

export async function cached<T>(
  key: string,
  ttlMs: number,
  loader: () => Promise<T>,
): Promise<T> {
  const now = Date.now();
  const hit = store.get(key) as Entry<T> | undefined;
  if (hit && hit.expires > now) return hit.value;

  const pending = inflight.get(key) as Promise<T> | undefined;
  if (pending) return pending;

  const promise = (async () => {
    try {
      const value = await loader();
      store.set(key, { value, expires: Date.now() + ttlMs });
      return value;
    } catch (err) {
      // Fall back to stale data rather than failing the render.
      if (hit) return hit.value;
      throw err;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}

/** Cache-Control for pages whose HTML can be served from the CDN edge. */
export const PAGE_CACHE_HEADERS = {
  "cache-control":
    "public, max-age=0, must-revalidate, s-maxage=600, stale-while-revalidate=86400",
};

/** Longer edge cache for large, rarely-changing index pages. */
export const ARCHIVE_CACHE_HEADERS = {
  "cache-control":
    "public, max-age=0, must-revalidate, s-maxage=3600, stale-while-revalidate=604800",
};
